import { INodeObject, ILinkObject } from "./common/ah.mb.common.node-object";
import { CommodityAndUnitsRec } from "./ah.mb.commodity-and-units-rec";

    export enum NodeCategory { Alloc, NonAlloc };

    export class LinkRule {

        nodeTypeName: string;
        nodeCategory: NodeCategory;

        maxOutputLinks: number;
        minOutputLinks: number;

        minInputLinks: number;
        maxInputLinks: number;

        commonIOCommodities: boolean;

        allowOutputs(): boolean {
            return this.maxOutputLinks !== 0;

        }

        allowInputs(): boolean {
            return this.maxInputLinks !== 0;
        }


        constructor(
            nodeTypeName: string,
            nodeCategory: NodeCategory,
            minInputLinks: number,
            maxInputLinks: number,
            minOutputLinks: number,
            maxOutputLinks: number,
            commonIOCommodities: boolean
        ) {

            this.nodeCategory = nodeCategory;
            this.nodeTypeName = nodeTypeName;
            this.minInputLinks = minInputLinks;
            this.maxInputLinks = maxInputLinks;
            this.minOutputLinks = minOutputLinks;
            this.maxOutputLinks = maxOutputLinks;
            this.commonIOCommodities = commonIOCommodities;
        }

        hasMaxInputs() {
            return this.maxInputLinks !== -1;

        }

        hasMaxOutputs() {
            return this.maxOutputLinks !== -1;
        }
    }

    export class LinkValidationResult {
        ok: boolean;
        msg: string;

        constructor(ok: boolean, msg: string) {
            this.ok = ok;
            this.msg = msg;
        }

    }

    export class LinkValidator {

        private readonly rules: LinkRule[];

        constructor(rules: LinkRule[]) {
            this.rules = rules;
        }

        private addRule(rule: LinkRule) {
            this.rules.push(rule);

        }

        getRuleForNodeType(nodeType: string): LinkRule {

            for (let i = 0; i < this.rules.length; i++) {
                if (this.rules[i].nodeTypeName === nodeType)
                    return this.rules[i];
            };

            return null;
        }

        // Validates a link
        validateLink(sourceNode: INodeObject,
            destNode: INodeObject,
            sourceLinks: ILinkObject[],
            destLinks: ILinkObject[],
            existingSrcCommodities: Array<CommodityAndUnitsRec>,
            existingDestCommodities: Array<CommodityAndUnitsRec>,
            newCommodity: CommodityAndUnitsRec): LinkValidationResult {

            const srcNodeRule = this.getRuleForNodeType(sourceNode.nodeType);

            const destNodeRule = this.getRuleForNodeType(destNode.nodeType);

            // Make a temporary copy of commodities, because we will have to modify it
            let tempSrcCommodities: CommodityAndUnitsRec[];

            // Different I/O nodes (refinery)
            if (srcNodeRule.commonIOCommodities) {
                tempSrcCommodities = existingSrcCommodities;
            }
            else
                {
               // Can remove input commodities for this one,
                // Because an input is allowed on the output 
                // Still have to be unique inputs and outputs tho
                    tempSrcCommodities = sourceLinks.map(link => {
                        return {
                            commodity: link.commodity,
                            units: link.units
                        };
                    });
            }

            let tempDestCommodities: CommodityAndUnitsRec[];
            // Same for dest as above
            if (destNodeRule.commonIOCommodities) {
                tempDestCommodities = existingDestCommodities;
            } else {
                tempDestCommodities = destLinks.map(link => {
                    return {
                        commodity: link.commodity,
                        units: link.units
                    };
                });
            }

            const validateTypesResult = this.internalValidateNodeTypeRules(srcNodeRule,
                destNodeRule,
                tempSrcCommodities,
                tempDestCommodities,
                newCommodity,
                sourceLinks,
                destLinks);

            if (!validateTypesResult.ok)
                return validateTypesResult;

            // Source outputs exceeded?
            if (srcNodeRule.hasMaxOutputs() && sourceLinks.length + 1 > srcNodeRule.maxOutputLinks)
                return new LinkValidationResult(false, "Source node maximum output link count exceeded");

            // Dest inputs exceeded
            if (destNodeRule.hasMaxInputs() && destLinks.length + 1 > destNodeRule.maxInputLinks)
                return new LinkValidationResult(false, "Destination node maximum input link count exceeded");

            // Nodes already linked?
            if (destLinks.filter((link) => {
                    return link.sourcePathAndName === sourceNode.pathAndName;
                })
                .length >
                0)
                return new LinkValidationResult(false, "Nodes are already linked");

            // Todo: Validate commodity names

            return new LinkValidationResult(true, "");
        }

        private internalValidateNodeTypeRules(srcNodeRule: LinkRule,
            destNodeRule: LinkRule,
            existingSrcCommodities: Array<CommodityAndUnitsRec>,
            existingDestCommodities: Array<CommodityAndUnitsRec>,
            newCommodity: CommodityAndUnitsRec,
            srcOutputs: ILinkObject[],
            destInputs: ILinkObject[]): LinkValidationResult {
            // Rule not found:
            if (srcNodeRule == null || destNodeRule == null)
                return new LinkValidationResult(false, "Unable to create link: unknown source or dest node type");

            // Src can link from?
            if (!srcNodeRule.allowOutputs())
                return new LinkValidationResult(false,
                    `Output links from nodes of type ${srcNodeRule.nodeTypeName} are not allowed`);

            // Dest can link to?
            if (!destNodeRule.allowInputs())
                return new LinkValidationResult(false,
                    `Input links to nodes of type ${destNodeRule.nodeTypeName} are not allowed`);


            // Nodes of same type?
            if (srcNodeRule.nodeCategory === destNodeRule.nodeCategory)
                return new LinkValidationResult(false, "Nodes of these types cannot be connected");

            // Different I/O commodities (refinery nodes only currently)
            if (!srcNodeRule.commonIOCommodities) {

                // Note: compare only against src outputs, ignoring src inputs
                if (srcOutputs.filter(
                        p => p.commodity === newCommodity.commodity && p.units === newCommodity.units)
                    .length >
                    0)
                return new LinkValidationResult(false, "Source node output commodities must be unique");
            }

            // Different I/O commodities (refinery nodes only currently)
            if (!destNodeRule.commonIOCommodities) {

                // Note: compare only against dest inputs, ignoring dest outputs
                if (destInputs.filter(
                        p => p.commodity === newCommodity.commodity && p.units === newCommodity.units)
                    .length >
                    0)
                    return new LinkValidationResult(false, "Destination node input commodities must be unique");
            }


            return new LinkValidationResult(true, "");

        }

        // Limited validation by node type only
        validateLinkByNodeTypes(srcNodeType: string,
            destNodeType: string,
            existingSrcCommodities: Array<CommodityAndUnitsRec>,
            existingDestCommodities: Array<CommodityAndUnitsRec>,
            newCommodity: CommodityAndUnitsRec,
            srcOutputs: ILinkObject[],
            destInputs: ILinkObject[]): LinkValidationResult {

            const srcNodeRule = this.getRuleForNodeType(srcNodeType);

            const destNodeRule = this.getRuleForNodeType(destNodeType);

            return this.internalValidateNodeTypeRules(srcNodeRule,
                destNodeRule,
                existingSrcCommodities,
                existingDestCommodities,
                newCommodity,
                srcOutputs,
                destInputs);
        }


        // Gets available commodities for a link
        getAvailableCommodities(list1: Array<CommodityAndUnitsRec>,
            list2: Array<CommodityAndUnitsRec>,
            srcNodeType: string,
            destNodeType: string): Array<CommodityAndUnitsRec> {
            let commodities = list2;

            // If source node has different I/O commodities, we cannot use it's inputs to guess decide the output
            const srcNodeRule = this.getRuleForNodeType(srcNodeType);
            const destNodeRule = this.getRuleForNodeType(destNodeType);

            // If source node only allows one kind of commodity, we can enforce this
            // In practice this is linking from hub to refining
            if (srcNodeRule && srcNodeRule.commonIOCommodities && list1.length === 1) {
                if (destNodeRule && !destNodeRule.commonIOCommodities)
                    return list1;
            }

            // Special rules for allocs, they always have a single commodity
            if (srcNodeRule && srcNodeRule.nodeCategory == NodeCategory.Alloc && list1.length === 1) 
                return list1;
            if (destNodeRule && destNodeRule.nodeCategory == NodeCategory.Alloc && list2.length === 1) 
                return list2;

            if (srcNodeRule && srcNodeRule.commonIOCommodities && list1.length > 0)
                commodities = commodities.concat(list1);

            // Ensure distinct commodioties
            const flags = [];
            const output = [];
            const l = commodities.length;
            let j;
            for (j = 0; j < l; j++) {
                if (flags[commodities[j].commodity]) continue;
                flags[commodities[j].commodity] = true;
                output.push(commodities[j]);
            }

            return output;

        }
    }



// WEBPACK FOOTER //
// ./ClientApp/scripts/ah.mb.link-validator.ts